/* React */
import React, { useCallback, useEffect, useState } from 'react';
import { IoTriangleSharp } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { GameHeader } from '../../../components/GameHeader';
import { Sidebar } from '../../../components/Sidebar';

import { useAuth } from '../../../hooks/auth';
import { usePopup } from '../../../hooks/popup';
import { useTimerContext } from '../../../hooks/TimerContext';
/* Interfaces & API */
import {
  getSavedSudoku,
  getSudoku,
  iBox,
  iSquare,
  postSudoku,
} from '../../../interfaces/sudoku';
/* Contexts & Hooks */
import { useSudoku } from '../SudokuContext';
/* Components */
import { Board } from './Board';
/* Styles */
import { Container, Content, GameContent, Rules } from './styles';

export const Sudoku: React.FC = () => {
  const { user } = useAuth(); // User Data
  const { mountPopup } = usePopup(); // PopUp Modal
  const { handleSetInitialTime, time, handleStopTimer, resetTimer } =
    useTimerContext();

  const {
    setIsSudokuFinished,
    score,
    setScore,
    getStart,
    handleShowAnswers,
    handleShowOneAnswer,
    matrix,
    handleCheckResults,
    setSolution,
    solution,
    numberOfHints,
    cleanGame,
  } = useSudoku();

  const [isLoading, setIsLoading] = useState(true);

  // Menu useState data
  const [maxScore, setMaxScore] = useState(0);

  // DADOS DO SUDOKU //

  // Altura e Largura os quadrados menores do Sudoku
  const [bigSquare, setBigSquare] = useState<iBox>();
  // Solução de todas as casas do Sudoku
  // As letras permitidas para cada casa do Sudoku
  const [alphabet, setAlphabet] = useState('');

  // DADOS SALVOS DO SUDOKU //
  // Se o usuário logado já terminou esse sudoku ou não
  const [isFinished, setHasFinished] = useState(false);
  // Matrix do jogo salvo
  const [savedMatrix, setSavedMatrix] = useState<iSquare[][]>([]);

  // Modal de regras //
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);

  const { id } = useParams();

  // Função para pegar os dados salvos do Sudoku
  const loadUserProgress = useCallback(async () => {
    if (user) {
      let progress = await getSavedSudoku({
        userID: Number(user?.id),
        gameID: id ? Number(id) : 0,
      });

      if (progress[0]) {
        let savedMatrix = JSON.parse(progress[0].cache);

        if (typeof savedMatrix === 'string') {
          savedMatrix = JSON.parse(savedMatrix);
        }

        setSavedMatrix(savedMatrix);
        setIsSudokuFinished(!!progress[0]?.finished);
        setScore(progress[0]?.pontuacao);

        if (!!progress[0]?.finished) {
          setHasFinished(true);
          handleStopTimer();
          handleSetInitialTime(progress[0]?.tempo);
        } else {
          setHasFinished(false);
        }
      }
    }
  }, [
    handleSetInitialTime,
    handleStopTimer,
    id,
    setIsSudokuFinished,
    setScore,
    user,
  ]);

  // Função para pegar a estrutura do Sudoku
  const loadSudoku = useCallback(async () => {
    setIsLoading(true);
    let response = await getSudoku(Number(id));

    let { attributes, cell } =
      response['crossword-compiler']['rectangular-puzzle'].sudoku.grid;
    setAlphabet(
      response['crossword-compiler']['rectangular-puzzle'].attributes.alphabet
    );

    const width = parseInt(attributes.width);
    const height = parseInt(attributes.height);

    setSolution(cell);

    getStart({
      width,
      height,
      cell,
    });

    setMaxScore((width * height - numberOfHints) * 2);
    setBigSquare(
      response['crossword-compiler']['rectangular-puzzle'].sudoku.attributes
    );

    setIsLoading(false);
  }, [getStart, id, numberOfHints, setSolution]);

  // Função de salvar o jogo.
  const handleSaveGame = useCallback(
    async (updatedScore?: number) => {
      if (user) {
        const requestBody = {
          jogos_id: id ? Number(id) : 0,
          users_id: Number(user.id),
          cache: matrix ? JSON.stringify(matrix) : '',
          finished: 1,
          pontuacao: updatedScore || score,
          tempo: time,
        };

        try {
          if (matrix && matrix[0][0]) {
            setSavedMatrix(matrix);
            await postSudoku(requestBody);
            setHasFinished(true);
            setIsSudokuFinished(true);
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
    [id, matrix, score, setIsSudokuFinished, time, user]
  );

  // Função para finalizar o jogo e checar os acertos.
  const handleHits = () => {
    mountPopup({
      title: 'Deseja realmente Finalizar o jogo?',
      message: 'Boa sorte!',
      type: 'alegre',
      buttons: [
        {
          buttonText: 'sim',
          buttonFunction: async () => {
            const updatedScore = handleCheckResults();
            handleStopTimer();

            await handleSaveGame(updatedScore);
          },
        },
        {
          buttonText: 'não',
          isPurple: true,
        },
      ],
    });
  };

  // Função para mostrar as respostas.
  // all: para todas e one: para uma
  const handleShowSudokuAnswers = (type: string) => {
    mountPopup({
      title:
        type === 'all'
          ? 'Deseja revelar as respostas?'
          : 'Deseja revelar a resposta nesse campo?',
      message: 'Se utilizar a ajuda, sua pontuação será zerada!',
      buttons: [
        {
          buttonText: 'sim',
          buttonFunction: () => {
            if (type === 'all') {
              handleShowAnswers();
            } else {
              handleShowOneAnswer();
            }
            handleSaveGame();
          },
        },
        {
          buttonText: 'não',
          isPurple: true,
        },
      ],
      type: 'atencao',
    });
  };

  // Função para limpar o jogo.
  const handleCleanGame = () => {
    mountPopup({
      title: 'Deseja realmente limpar o jogo?',
      message: 'Se sim, a pontuação será zerada e o jogo recomeçará do início!',
      buttons: [
        {
          buttonText: 'sim',
          buttonFunction: () => {
            resetTimer();
            cleanGame();
          },
        },
        {
          buttonText: 'não',
          isPurple: true,
        },
      ],
      type: 'atencao',
    });
  };

  // useEffect para carregar os dados do Sudoku
  useEffect(() => {
    loadSudoku();
    loadUserProgress();
  }, [loadUserProgress, loadSudoku]);

  useEffect(() => {
    if (isFinished) {
      mountPopup({
        title: 'Jogo concluído!',
        message: 'Você concluiu este passatempo, parabéns!',
        buttons: [
          {
            buttonText: 'ok!',
            isPurple: true,
          },
        ],
        type: 'sucesso',
      });
    }
  }, [isFinished, mountPopup]);

  return (
    <Container>
      <GameHeader
        type="SUDOKU"
        maxScore={maxScore}
        menuItems={[
          {
            buttonText: 'Verificar Acertos',
            buttonFunctions: () => {
              handleHits();
            },
          },
          {
            buttonText: 'Revelar Resposta',
            buttonFunctions: () => {
              handleShowSudokuAnswers('all');
            },
          },
          {
            buttonText: 'Revelar Número',
            buttonFunctions: () => {
              handleShowSudokuAnswers('one');
            },
          },
          {
            buttonText: !isFinished ? 'Limpar jogo' : 'Recomeçar jogo',
            buttonFunctions: () => {
              handleCleanGame();
            },
          },
          {
            buttonText: 'Regras',
            buttonFunctions: () => {
              setIsRulesModalOpen(true);
            },
          },
        ]}
      />
      <Content>
        {!isLoading && (
          <>
            <GameContent>
              <div>
                <div>
                  {bigSquare && (
                    <Board
                      cellSolution={solution}
                      bigSquare={bigSquare}
                      alphabet={alphabet}
                      savedMatrix={savedMatrix}
                    />
                  )}
                </div>

                <Sidebar
                  type="SUDOKU"
                  maxScore={maxScore}
                  menuItems={[
                    {
                      buttonText: 'Verificar Acertos',
                      buttonFunctions: () => {
                        handleHits();
                      },
                    },
                    {
                      buttonText: 'Revelar Resposta',
                      buttonFunctions: () => {
                        handleShowSudokuAnswers('all');
                      },
                    },
                    {
                      buttonText: 'Revelar Número',
                      buttonFunctions: () => {
                        handleShowSudokuAnswers('one');
                      },
                    },
                    {
                      buttonText: !isFinished
                        ? 'Limpar jogo'
                        : 'Recomeçar jogo',
                      buttonFunctions: () => {
                        handleCleanGame();
                      },
                    },
                    {
                      buttonText: 'Regras',
                      buttonFunctions: () => {
                        setIsRulesModalOpen(true);
                      },
                    },
                  ]}
                />
              </div>
            </GameContent>
            <p style={{ background: '#D1A14C' }}>
              VER MAIS COMENTÁRIOS <IoTriangleSharp size={12} />
            </p>

            <Rules isVisible={isRulesModalOpen}>
              <div>
                <header>
                  <h1>Regras</h1>
                  <button onClick={() => setIsRulesModalOpen(false)}>
                    <h1>X</h1>
                  </button>
                </header>
                <main>
                  <span>
                    O objetivo do jogo é preencher e completar todos os
                    quadrados da tabela com os números de 1 a 9. O desafio se
                    encontra nas restrições impostas pelas regras do Sudoku, que
                    podem ser lidas abaixo:
                  </span>
                  <br /> <br />
                  <span>
                    <strong>
                      1ª Regra: Cada linha deve conter os números de 1 a 9, sem
                      repetir!
                    </strong>
                    <br />
                  </span>
                  <span>
                    O jogador deve preencher cada linha da tabela de modo que
                    não existam números duplicados no final, no entanto, a ordem
                    númerica é irrelevante.
                  </span>
                  <br /> <br />
                  <span>
                    <strong>
                      2ª Regra: Cada coluna também deve conter os números de 1 a
                      9, sem repetições!
                    </strong>
                    <br />
                  </span>
                  <span>
                    O jogador deve preencher cada coluna da tabela com números
                    de 1 a 9 sem repetições por coluna!
                  </span>
                  <br /> <br />
                  <span>
                    <strong>
                      3ª Regra: Cada número pode estar presente apenas uma vez
                      por grupo!
                    </strong>
                    <br />
                  </span>
                  <span>
                    Uma tabela de Sudoku tradicional é formada por 9 linhas e 9
                    colunas (9 x 9), e é divida em 9 grupos menores de 3 linhas
                    e 3 colunas (3 x 3). Os números de 1 a 9 podem estar
                    presentes apenas uma vez em cada um destes grupos.
                  </span>
                  <br /> <br />
                  <span>
                    <strong>
                      4ª Regra: O valor da soma de cada linha, coluna ou grupo é
                      de 45!
                    </strong>
                    <br />
                  </span>
                  <span>
                    Para descobrir quais os números faltantes em cada linha,
                    coluna ou grupo, o jogador pode contá-los ou então somá-los!
                    Quando não existem dígitos duplicados, a soma individual de
                    cada linha, coluna ou grupo é sempre 45:
                    <br />
                    <footer>
                      <span>1 + 2 + 3 + 4 + 5 + 6 + 7 + 8 + 9 = 45</span>
                    </footer>
                  </span>
                </main>
              </div>
            </Rules>
          </>
        )}
      </Content>
    </Container>
  );
};
